import { defineStore } from 'pinia'
import * as authService from '@/modules/auth/services/auth.service'
import type {
  User,
  LoginPayload,
  LoginResponse,
  RegisterPayload,
  AuthState,
} from '../types/auth.types'
import { AuthStatusEnum } from '@/modules/auth/enums/auth-status.enum'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: localStorage.getItem('auth_token'),
    isLoading: false,
    error: null,
  }),

  getters: {
    isAuthenticated(state): boolean {
      return !!state.token && !!state.user
    },

    fullName(state): string {
      if (!state.user) return 'Usuario'
      return `${state.user.first_name} ${state.user.last_name}`.trim()
    },

    userRole(state): string | null {
      return state.user?.roles?.[0] || null
    },

    hasRole(state) {
      return (role: string): boolean => {
        return state.user?.roles?.includes(role) || false
      }
    },

    hasError(state): boolean {
      return !!state.error
    },
  },

  actions: {
    async login(payload: LoginPayload): Promise<void> {
      this.error = null
      this.isLoading = true

      try {
        const response: LoginResponse = await authService.login(payload)

        if (!response || !response.access_token || !response.user) {
          throw new Error('Respuesta del servidor inválida')
        }

        this.token = response.access_token
        localStorage.setItem('auth_token', response.access_token)

        this.user = response.user
        localStorage.setItem('user_data', JSON.stringify(response.user))

      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Error al iniciar sesión'
        this.error = errorMessage

        if (import.meta.env.DEV) {
          console.error('❌ Error en login:', errorMessage, err)
        }

        this.user = null
        this.token = null
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')

        throw new Error(errorMessage)
      } finally {
        this.isLoading = false
      }
    },

    async register(userData: RegisterPayload): Promise<void> {
      this.error = null
      this.isLoading = true

      try {
        const response = await authService.register(userData)

        if (!response || !response.access_token || !response.user) {
          throw new Error('Respuesta del servidor inválida')
        }

        this.token = response.access_token
        localStorage.setItem('auth_token', response.access_token)

        this.user = response.user
        localStorage.setItem('user_data', JSON.stringify(response.user))

      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || err.message || 'Error al registrar usuario'
        this.error = errorMessage

        if (import.meta.env.DEV) {
          console.error('❌ Error en registro:', errorMessage, err)
        }

        this.user = null
        this.token = null
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')

        throw new Error(errorMessage)
      } finally {
        this.isLoading = false
      }
    },

    logout(): void {
      this.user = null
      this.token = null
      this.error = null
      this.isLoading = false

      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
    },

    restoreSession(): boolean {
      const token = localStorage.getItem('auth_token')
      const userData = localStorage.getItem('user_data')

      if (token && userData) {
        try {
          this.token = token
          this.user = JSON.parse(userData)

          return true
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('❌ Error al restaurar sesión:', error)
          }
          this.logout()
          return false
        }
      }

      return false
    },

    clearError(): void {
      this.error = null
    },

    setLoading(loading: boolean): void {
      this.isLoading = loading
    },
  },
})
